// ============================================================================
// Notifications & activity awareness
// ============================================================================
// Three layers, covering three visibility states:
//  1. Tab open but backgrounded → title badge + favicon dot (always on)
//  2. Browser minimized / other app focused → OS notification via the
//     Notification API (opt-in, per-event toggles in Settings)
//  3. Tab closed → Web Push from the server (see src/push.ts; the SW's
//     push handler in sw.js displays it, notificationclick focuses/reopens
//     the app on the deep-linked session)

import { state } from "./state.js";
import { updateTabTitle, tabCurrentName } from "./tabtitle.js";

// ---- Layer 1: unread badge in title + favicon ----

/** Notification title: "<session name> — <base>" when a display name is
 *  known (set from sessions_list), else just the base. */
export function notifTitle(base) {
  const name = tabCurrentName();
  return name ? name + " — " + base : base;
}

/** Count an event the user missed (only while the tab is hidden) and
 *  reflect it in the document title + favicon. */
export function bumpUnread() {
  if (!document.hidden) return;
  state.unreadEvents++;
  updateTabTitle();
  setFaviconBadge(true);
}

/** Swap the favicon between the plain and badged variants. */
function setFaviconBadge(on) {
  const links = document.querySelectorAll('link[rel="icon"]');
  for (const l of links) {
    if (!(l instanceof HTMLLinkElement)) continue;
    const isBadge = (l.getAttribute("href") || "").includes("icon-badge");
    l.disabled = on ? !isBadge : isBadge;
  }
}

document.addEventListener("visibilitychange", () => {
  if (!document.hidden) {
    state.unreadEvents = 0;
    updateTabTitle();
    setFaviconBadge(false);
  }
});

// ---- Layer 2: OS notifications from in-tab events (tab must be open) ----

/** Per-event notification prefs, mirrored to localStorage by Settings. */
function notifPrefs() {
  const get = (k, d) => {
    const v = localStorage.getItem(k);
    return v === null ? d : v === "1";
  };
  return {
    turn: get("devin-notif-turn", true),
    perm: get("devin-notif-perm", true),
    error: get("devin-notif-error", false),
    tool: get("devin-notif-tool", true),
    sound: get("devin-notif-sound", true),
  };
}

/** Fire an OS-level notification and sound for a missed event. */
export function maybeNotify(type, title, body) {
  const isBackground = document.hidden || (typeof document.hasFocus === "function" && !document.hasFocus());
  if (!isBackground) return;
  const prefs = notifPrefs();
  const prefKey =
    type === "turn_end" ? "turn" :
    type === "permission_needed" ? "perm" :
    type === "error" ? "error" :
    type === "tool_attention" ? "tool" : null;
  if (!prefKey || !prefs[prefKey]) return;

  if (prefs.sound && typeof playNotificationSound === "function") {
    const soundKind = type === "permission_needed" ? "prompt" : type === "error" ? "error" : "done";
    playNotificationSound(soundKind);
  }

  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      tag: `devin-${state.currentSessionId}-${type}`,
      icon: "/icon.svg",
      badge: "/icon-badge.svg",
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* some platforms restrict Notification construction */ }
}

// ---- Layer 3: Web Push subscription (server reaches a CLOSED tab) ----

/** base64url → Uint8Array for pushManager.subscribe's applicationServerKey. */
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Ensure a Web Push subscription exists and is registered with the server. */
export async function ensurePushSubscription() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const vapid = await apiJson("/api/push/vapid-public");
      if (!vapid?.publicKey) return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
      });
    }
    await apiJson("/api/push/subscribe", {
      method: "POST",
      body: sub.toJSON(),
    });
  } catch (e) {
    console.warn("[push] subscription failed:", e?.message || e);
  }
}

/**
 * Initialize push notification handling.
 * @param {function} loadSessionFromHistory - function to switch sessions
 */
export function initNotifications(loadSessionFromHistory) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (ev) => {
      const sid = ev?.data?.kind === "load-session" && ev.data.sessionId;
      if (sid && sid !== state.currentSessionId) loadSessionFromHistory(sid);
    });
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      ensurePushSubscription();
    }
  }
}
